import { useCallback, useEffect, useRef, useState } from "react";

import { useObjectMemo } from "../../../hooks";
import { useRequestWithFeedback } from "../../../composites";
import { REQUEST_STATUS } from "../../../composites/request/request.types";
import { useAppDispatch } from "../utils";

import { API } from "./api";
import { actions } from "./slice";
import { useACHCompany } from "./hooks";

import type { BootstrapFetchRequest } from "../../bootstrap";

export const useFetchACHCompanies = () => {
  const { send, status } = useRequestWithFeedback<
    API.FetchACHCompanies.Response,
    API.FetchACHCompanies.Error
  >();

  const dispatch = useAppDispatch();
  const fetchACHCompanies = useCallback(
    (filterQuery?: string) => {
      send({
        action: API.fetchACHCompanies(filterQuery),
        onData: ({ results: achCompanies }) => {
          dispatch(actions.setAll(achCompanies));
        },
      });
    },
    [dispatch, send],
  );

  const hasData = !!useACHCompany();

  return useObjectMemo({
    error: status === REQUEST_STATUS.error,
    send: fetchACHCompanies,
    loading:
      status === REQUEST_STATUS.idle || status === REQUEST_STATUS.loading,
    hasData,
  }) satisfies BootstrapFetchRequest;
};

export const useACHCompanyForAccount = (accountId?: string) => {
  const { send, loading } = useRequestWithFeedback<
    API.FetchACHCompanies.Response,
    API.FetchACHCompanies.Error
  >();
  const dispatch = useAppDispatch();

  const [resolved, setResolved] = useState<{
    accountId: string;
    hasACHCompany: boolean;
  }>();
  // the account whose lookup `send` has actually accepted
  const requestedForRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!accountId) return;
    if (resolved?.accountId === accountId) return;
    if (requestedForRef.current === accountId) return;
    if (loading) return;
    requestedForRef.current = accountId;
    send({
      action: API.fetchACHCompanies(`institution_account=${accountId}`),
      onSuccess: ({ results: achCompanies }) => {
        dispatch(actions.setAll(achCompanies));
        setResolved({ accountId, hasACHCompany: achCompanies.length > 0 });
      },
      onError: () => {
        // a failed lookup must not leave the previous account's company in the
        // store, or submit would originate under the wrong company
        dispatch(actions.setAll([]));
        setResolved({ accountId, hasACHCompany: false });
      },
    });
  }, [accountId, resolved, loading, send, dispatch]);

  const isResolved = Boolean(accountId) && resolved?.accountId === accountId;

  return useObjectMemo({
    loading: Boolean(accountId) && !isResolved,
    hasACHCompany: isResolved && Boolean(resolved?.hasACHCompany),
    isACHCompanyMissing: isResolved && !resolved?.hasACHCompany,
  });
};
